import {
  createContext,
  forwardRef,
  type ForwardedRef,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { useDeviceModeContext } from 'hb-builder/contexts/DeviceModeContext';
import { type BuilderComponent, type ComponentType } from 'hb-builder/types';
import { type DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

type ComponentContextType = ComponentProviderProps & {
  ref: ForwardedRef<HTMLElement>;
};

export interface ComponentProviderProps {
  canBeDoubleClicked: BuilderComponent['canBeDoubleClicked'];
  canBeDragged: BuilderComponent['canBeDragged'];
  canBeSelected: BuilderComponent['canBeSelected'];
  isInLastColumn: boolean;
  isSelected: boolean;
  item: DocumentItem;
  itemIndex: number;
  label: string;
  nbParentChildren: number;
  toolbar: BuilderComponent['toolbar'];
  toolbarMobile?: BuilderComponent['toolbarMobile'];
  type: ComponentType;
}

const ComponentContext = createContext<ComponentContextType | undefined>(
  undefined,
);

export const ComponentProvider = forwardRef<
  HTMLElement,
  PropsWithChildren<ComponentProviderProps>
>(
  (
    {
      children,
      canBeDoubleClicked,
      canBeDragged,
      canBeSelected,
      ...otherProps
    },
    ref,
  ) => {
    const { isDesktopMode } = useDeviceModeContext();

    const value: ComponentContextType = useMemo(
      () => ({
        canBeDoubleClicked: isDesktopMode && canBeDoubleClicked,
        canBeDragged: isDesktopMode && canBeDragged,
        canBeSelected,
        ...otherProps,
        ref,
      }),
      [
        isDesktopMode,
        canBeDoubleClicked,
        canBeDragged,
        canBeSelected,
        otherProps,
        ref,
      ],
    );

    return (
      <ComponentContext.Provider value={value}>
        {children}
      </ComponentContext.Provider>
    );
  },
);

export const useComponentContext = () =>
  useContext(ComponentContext) as ComponentContextType;

export default ComponentContext;
